import React from "react";
import "./SharingOptions.css";
import LinkAccessModal from "./LinkAccessModal";

/** Set to true when restricted link-access options are implemented. */
const SHOW_LINK_ACCESS_OPTIONS = false;
/** Set to true when people-with-access invites are implemented. */
const SHOW_PEOPLE_ACCESS = false;

const SharingOptions = ({
  onClose = () => {},
  onShare = () => {},
  onCopyLink = () => {},
  shareUrl = null,
  sharing = false,
  errorMessage = null,
  successMessage = null,
}) => {
  const [linkAccess, setLinkAccess] = React.useState("anyone-with-link");
  const [peopleEmail, setPeopleEmail] = React.useState("");
  const [showLinkAccessModal, setShowLinkAccessModal] = React.useState(false);
  const [modalPosition, setModalPosition] = React.useState({ top: 0, left: 0 });
  const [sharedPeople, setSharedPeople] = React.useState([
    {
      id: 1,
      name: "Naveen Kumar",
      email: "naveen@gmail.com",
      role: "AUTHOR",
      status: "",
    },
    {
      id: 2,
      name: "mohan.krishna@gmail.com",
      email: "mohan.krishna@gmail.com",
      role: "can edit",
      status: "Invited",
    },
  ]);
  const linkAccessButtonRef = React.useRef(null);

  const linkAccessOptions = [
    {
      id: "anyone-with-link",
      label: "Anyone with the link",
      description: "Anyone with the link can view this design in the editor.",
    },
    ...(SHOW_LINK_ACCESS_OPTIONS
      ? [
          {
            id: "people-added",
            label: "Only people added can access",
            description: "Only people with access to this design can use this link.",
          },
          {
            id: "only-you",
            label: "Only you can access",
            description: "Only you can access using this link.",
          },
        ]
      : []),
  ];

  const handleCopyLink = () => {
    onShare({ linkAccess });
  };

  const handleAddPerson = () => {
    if (peopleEmail.trim()) {
      setSharedPeople([
        ...sharedPeople,
        {
          id: sharedPeople.length + 1,
          name: peopleEmail,
          email: peopleEmail,
          role: "can edit",
          status: "Invited",
        },
      ]);
      setPeopleEmail("");
    }
  };

  const getLinkAccessLabel = () => {
    return linkAccessOptions.find((opt) => opt.id === linkAccess)?.label || "Select access";
  };

  return (
    <div className="sharing-options-overlay" onClick={onClose}>
      <div
        className="sharing-options-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sharing-options-header">
          <h2>Share this design</h2>
          <button className="close-btn" onClick={onClose} disabled={sharing}>
            ✕
          </button>
        </div>

        <div className="sharing-options-content">
          <div className="form-group">
            <label className="form-label">Link access</label>
            <div className="link-access-field-wrapper">
              {SHOW_LINK_ACCESS_OPTIONS ? (
                <>
                  <button
                    ref={linkAccessButtonRef}
                    className="link-access-button"
                    onClick={() => {
                      if (linkAccessButtonRef.current) {
                        const rect = linkAccessButtonRef.current.getBoundingClientRect();
                        setModalPosition({
                          top: rect.bottom + 8,
                          left: rect.left,
                          width: rect.width,
                        });
                      }
                      setShowLinkAccessModal(true);
                    }}
                    disabled={sharing}
                  >
                    <span>{getLinkAccessLabel()}</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {showLinkAccessModal && (
                    <LinkAccessModal
                      isOpen={showLinkAccessModal}
                      selectedAccess={linkAccess}
                      onSelectAccess={setLinkAccess}
                      onClose={() => setShowLinkAccessModal(false)}
                      position={modalPosition}
                      showRestrictedOptions={SHOW_LINK_ACCESS_OPTIONS}
                    />
                  )}
                </>
              ) : (
                <div className="link-access-button link-access-button--static">
                  <span>{getLinkAccessLabel()}</span>
                </div>
              )}
            </div>
            <div className="link-access-description">
              {linkAccessOptions.find((opt) => opt.id === linkAccess)?.description}
            </div>
          </div>

          {errorMessage && (
            <p className="sharing-options-error" role="alert">
              {errorMessage}
            </p>
          )}
          {successMessage && (
            <p className="sharing-options-success" role="status">
              {successMessage}
            </p>
          )}

          {shareUrl ? (
            <div className="share-link-result">
              <input
                type="text"
                className="share-link-input"
                value={shareUrl}
                readOnly
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                className="copy-share-btn"
                onClick={onCopyLink}
              >
                Copy
              </button>
            </div>
          ) : (
            <button
              className="copy-share-btn"
              onClick={handleCopyLink}
              disabled={sharing}
            >
              {sharing ? "Creating link…" : "Copy & Share Link"}
            </button>
          )}

          {SHOW_PEOPLE_ACCESS && (
            <div className="form-group">
              <label className="form-label">People with access</label>
              <div className="people-input-wrapper">
                <input
                  type="text"
                  className="people-email-input"
                  value={peopleEmail}
                  onChange={(e) => setPeopleEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddPerson();
                    }
                  }}
                  placeholder="Add name or Email"
                />
              </div>

              <div className="people-list">
                {sharedPeople.map((person) => (
                  <div key={person.id} className="people-item">
                    <div className="people-avatar">
                      {person.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="people-info">
                      <div className="people-name">{person.name}</div>
                      <div className="people-email">{person.email}</div>
                    </div>
                    <div className="people-role">
                      <div className="people-role-label">{person.role}</div>
                      {person.status && (
                        <div className="people-status">{person.status}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharingOptions;
